import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const BENCHMARKS = [
  {
    type: 'New / personal account',
    ratio: '1:1 to 2:1',
    note: 'When you\'re just starting out, it\'s normal to follow more people than follow you. Aim to grow your followers faster than your following.',
  },
  {
    type: 'Growing creator / niche account',
    ratio: '3:1 to 10:1',
    note: 'As your content gains traction, your follower count should significantly outpace who you follow. This signals authority in your niche.',
  },
  {
    type: 'Established influencer / brand',
    ratio: '10:1 or higher',
    note: 'Accounts with large audiences typically follow very few people relative to their following count. This ratio signals credibility and reach.',
  },
  {
    type: 'Business account',
    ratio: '5:1 or higher',
    note: 'Businesses should generally follow industry accounts, partners, and relevant voices — not everyone who follows them back.',
  },
];

const GoodFollowerRatio: React.FC = () => {
  useSEO(
    'What Is a Good Instagram Follower-to-Following Ratio? — Purgr',
    'Wondering if your Instagram follower-to-following ratio is healthy? Here\'s what the numbers mean, benchmarks by account type, and how to improve yours.'
  );

  return (
    <PageLayout title="What Is a Good Instagram Follower-to-Following Ratio?">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">

        <p className="text-white/80 text-base">
          Your Instagram follower ratio tells visitors a lot about your account before they read a single post.
          A healthy ratio signals credibility and organic growth. A poor ratio can raise instant red flags.
          Here's how to benchmark yourself and what to do about it.
        </p>

        {/* How to calculate */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-3 text-base">How to calculate your follower ratio</p>
          <p className="text-white/55 mb-3 text-xs">
            Your follower-to-following ratio is calculated by dividing your followers by the number of accounts you follow:
          </p>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <p className="text-white/80 font-mono text-sm">Follower Ratio = Followers ÷ Following</p>
            <p className="text-white/40 text-xs mt-2">Example: 2,500 followers ÷ 500 following = <strong className="text-white/60">5.0 ratio</strong></p>
          </div>
          <p className="text-white/50 text-xs mt-3">
            A ratio above 1.0 means more people follow you than you follow. Below 1.0 means you follow more
            than follow you back — a sign that growth may be driven by follow-for-follow tactics.
          </p>
        </div>

        {/* Benchmarks */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">Ratio benchmarks by account type</p>
          <div className="space-y-3">
            {BENCHMARKS.map(({ type, ratio, note }) => (
              <div key={type} className="glass-card p-5">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-white/80 font-semibold text-sm">{type}</p>
                  <span className="text-xs ig-gradient-bg text-white px-3 py-1 rounded-full font-bold">{ratio}</span>
                </div>
                <p className="text-white/50 text-xs leading-relaxed">{note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What about celebrities */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-2">What about celebrities and mega-influencers?</p>
          <p className="text-white/55 text-xs leading-relaxed">
            Accounts with millions of followers often have ratios of 100:1 or higher — some follow under 100
            people while having millions of followers. These ratios aren't realistic targets for most accounts,
            but they illustrate how ratio scales with genuine influence. Don't compare your early-stage account
            to these benchmarks.
          </p>
        </div>

        {/* Signs of a bad ratio */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">Signs your ratio needs attention</p>
          <div className="space-y-2">
            {[
              'You follow more than 1,000 accounts but have under 500 followers',
              'Your following count has grown faster than your follower count over time',
              'You\'ve used follow-for-follow tactics in the past',
              'You follow many accounts that haven\'t posted in months',
              'A large portion of accounts you follow don\'t follow you back',
            ].map((sign) => (
              <div key={sign} className="glass-card p-3 flex items-start gap-3">
                <span className="text-red-400/60 flex-shrink-0 mt-0.5">⚠</span>
                <p className="text-white/55 text-xs">{sign}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How to fix it */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">How to fix a bad ratio</p>
          <p className="text-white/60 mb-3">
            Improving your ratio comes down to two things: reducing who you follow and growing who follows you.
          </p>
          <div className="space-y-3">
            <div className="glass-card p-4">
              <p className="text-white/80 font-semibold text-sm mb-2">Step 1 — Find who's not following you back</p>
              <p className="text-white/50 text-xs mb-2">
                Use Purgr to upload your Instagram data files. You'll instantly see everyone you follow
                who doesn't follow you back — no Instagram login required.
              </p>
              <Link to="/" className="text-white/50 hover:text-white/80 text-xs underline transition-colors">
                Try Purgr free →
              </Link>
            </div>
            <div className="glass-card p-4">
              <p className="text-white/80 font-semibold text-sm mb-2">Step 2 — Unfollow strategically</p>
              <p className="text-white/50 text-xs">
                Go through your non-followers list and manually unfollow accounts you don't have a genuine
                reason to follow. Stay under ~150–200 unfollows per day to avoid Instagram's action limits.
              </p>
            </div>
            <div className="glass-card p-4">
              <p className="text-white/80 font-semibold text-sm mb-2">Step 3 — Focus on content that grows followers</p>
              <p className="text-white/50 text-xs">
                Post Reels consistently, use targeted hashtags, and engage with your niche community.
                Growing your followers is the other side of improving your ratio.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">See who's not following you back</p>
            <p className="text-white/50 text-sm mb-4">The first step to a better ratio — free, private, and instant.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Try Purgr Free →
            </Link>
          </div>
        </div>

        {/* Related guides */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/how-to-improve-instagram-follower-ratio" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to improve your follower ratio →</p>
            </Link>
            <Link to="/how-to-clean-up-your-following-list" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to clean up your following list →</p>
            </Link>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default GoodFollowerRatio;
